// =============================================================================
// QBO post-failure classifier.
//
// `qbo-post-transaction` writes the raw error string from the edge function
// into `transactions.qb_last_error` on any failure. That string is precise
// for an engineer reading the row in the DB ("QBO /purchase 401: …") but
// useless for an operator trying to act on it from the Transactions grid —
// they want to know whether to reconnect QuickBooks, map the bank account,
// pick a different category, or just click Retry.
//
// This classifier walks the message and returns a structured next-action
// recommendation the UI can render as a single button on the row (and as a
// richer banner on the Transaction Detail page). The mapping is one-way
// (string → bucket) and forgiving: anything we don't recognise falls
// through to a generic Retry, which is the historical behaviour.
//
// Keep the regex anchors as forgiving as the underlying error strings will
// allow — the edge function tweaks wording over time and we'd rather show
// the right button than a stale Retry. Specific buckets (auth, bank
// mapping, category) always win over the catch-all 'qbo_validation' bucket
// when they could both match.
//
// Mirrors the error sites in `supabase/functions/qbo-post-transaction/index.ts`
// and the auth helpers in `supabase/functions/_shared/qbo.ts`. When that
// code grows a new failure mode, add a matching case here too.
// =============================================================================

export type QboPostFailureKind =
  // OAuth / connection issues — operator needs to reconnect QuickBooks.
  | 'reconnect_quickbooks'
  // bank_accounts.qb_account_id is null or its CoA row has no external_id.
  // Fix on /financials/bank-accounts.
  | 'map_bank_account'
  // Transaction has no qb_account_id assigned.
  | 'assign_category'
  // qb_account_id resolves to a CoA row whose account_type the classifier
  // can't route ("Unsupported Category account_type …"). Operator picks a
  // different category.
  | 'unsupported_category'
  // The picked category resolves to a QBO Account QBO no longer accepts —
  // most commonly because the Account was deleted/inactivated in QBO and
  // we haven't re-synced. Surfaces as a 400 with "Invalid Reference Id"
  // or similar; operator re-syncs CoA or picks a different category.
  | 'qbo_reference_invalid'
  // expense_status !== 'approved'. Operator approves it in Expense
  // Tracking. (Rare for the failed bucket — the trigger usually keeps
  // unapproved rows at 'awaiting_approval' — but the explicit
  // transaction_ids path can still hit this.)
  | 'approve_expense'
  // expense_status === 'in_qb'. Operator flips the status off the
  // "Already in QuickBooks" terminal.
  | 'mark_not_in_qb'
  // Amazon Business order has no categorisable lines. Operator opens
  // /financials/amazon-business and picks per-line categories.
  | 'categorise_amazon_lines'
  // Mercury / Plaid marked the transaction as failed/cancelled/etc.
  // Defense in depth — the bank-status trigger should keep these out of
  // the failed bucket entirely, but the explicit transaction_ids path
  // could still surface one.
  | 'bank_did_not_settle'
  // Splits sum doesn't equal the parent's amount_cents (defense in
  // depth — the set_transaction_splits RPC validates the same invariant
  // at write time, so this only fires when an out-of-band write
  // produced an inconsistent split set). Operator opens the detail
  // page to fix the splits.
  | 'splits_sum_mismatch'
  // Operator tried to post a Transfer / CreditCardPaymentTxn-routed
  // row that has manual splits. QBO's Transfer / CCP entities are
  // single-amount and don't accept multi-line; operator clears the
  // splits or picks a different parent category.
  | 'splits_unsupported_kind'
  // QBO returned 4xx with a validation message (missing field, bad
  // shape, etc.). Operator opens the detail page where the AI category
  // panel + editable fields can help them figure out what to change.
  | 'qbo_validation'
  // QBO 5xx or transient connectivity failure. A blind Retry is the
  // right action — `qbo-post-transaction` is idempotent on
  // `qb_external_id` + `qb_request_id`.
  | 'qbo_server'
  // The booked QBO entity is gone — the edge function already handles
  // this by auto-recreating, so this bucket only fires when the auto-
  // recreate itself failed. Retry is the right action.
  | 'qbo_object_missing'
  // Supabase Storage download failed (receipt couldn't be fetched for
  // the mirror upload). Doesn't fail the post itself — but if it does
  // surface here, retry is the right action.
  | 'storage_error'
  // Atlas DB write failed mid-post (e.g. trigger rejection, RLS misconfig).
  // Retry is safe — the function is idempotent on qb_external_id.
  | 'db_error'
  // No qb_last_error captured (the row says 'failed' but the error
  // string is null/empty). Shouldn't happen post-migration 67 but we
  // still want a sane fallback.
  | 'no_details'
  // Everything else — generic Retry.
  | 'unknown';

export type QboPostFailureTone = 'warning' | 'danger' | 'info';

export type QboPostFailureAction =
  | {
      kind: 'navigate';
      /** Short button label, kept under ~14 chars so it fits a grid cell. */
      label: string;
      /** Internal route. Caller renders a `<Link>` (or `<Button asChild>`). */
      to: string;
      /** Longer label for the detail-page banner button. */
      longLabel: string;
    }
  | {
      kind: 'open_detail';
      label: string;
      longLabel: string;
    }
  | {
      kind: 'retry';
      label: string;
      longLabel: string;
    }
  | { kind: 'none' };

export interface QboPostFailure {
  kind: QboPostFailureKind;
  /** Two- to four-word headline for the row pill / banner title. */
  headline: string;
  /** Single-sentence explanation suitable for a tooltip or banner body. */
  description: string;
  /** Operator's next action — render as a button on the row + banner. */
  action: QboPostFailureAction;
  /** Banner / row colouring hint. */
  tone: QboPostFailureTone;
}

/**
 * Classify a `transactions.qb_last_error` string into a structured next-action.
 * Pass the raw `qb_last_error` value (which may be null when the row is
 * failed but no message was recorded). Always returns a value — falls
 * through to a generic Retry recommendation when no specific bucket matches.
 *
 * Pure and synchronous; callers can invoke per-row inside render without a
 * memo budget concern.
 */
export function classifyQboPostFailure(rawError: string | null | undefined): QboPostFailure {
  const error = (rawError ?? '').trim();
  if (!error) {
    return {
      kind: 'no_details',
      headline: 'Post failed',
      description:
        "QuickBooks rejected the post but Atlas didn't capture an error message. " +
        'Click Retry — if it fails again, open the row to see fresh details.',
      action: {
        kind: 'retry',
        label: 'Retry',
        longLabel: 'Retry post',
      },
      tone: 'warning',
    };
  }

  // --- Auth / connection -----------------------------------------------------
  // Order matters: every auth failure also tends to be a `QBO …` message, so
  // match these BEFORE the generic QBO-validation bucket below.
  if (
    /quickbooks not connected/i.test(error) ||
    /Missing QBO refresh token/i.test(error) ||
    /QBO token refresh/i.test(error) ||
    /QBO_CLIENT_(ID|SECRET) missing/i.test(error) ||
    // /v3/company endpoints return 401 with "AuthenticationFailed" /
    // "invalid_grant" when the access token has expired AND the refresh
    // dance can't recover. Match the bare 401 too — the detail string from
    // QBO for an expired-token call is usually empty.
    /QBO\b[^\n]*\b401\b/i.test(error) ||
    /authenticationfailed|invalid_grant|invalid_token/i.test(error)
  ) {
    return {
      kind: 'reconnect_quickbooks',
      headline: 'Reconnect QuickBooks',
      description:
        "Atlas's QuickBooks connection has expired or been revoked. Reconnect on " +
        'the Integrations page and click Retry.',
      action: {
        kind: 'navigate',
        label: 'Reconnect',
        longLabel: 'Reconnect QuickBooks',
        to: '/integrations',
      },
      tone: 'danger',
    };
  }

  // --- Bank account mapping --------------------------------------------------
  if (/has no QBO mapping/i.test(error)) {
    return {
      kind: 'map_bank_account',
      headline: 'Map bank account',
      description:
        "This row's bank account isn't mapped to a QuickBooks register. Set " +
        'the mapping on the Bank Accounts page, then click Retry.',
      action: {
        kind: 'navigate',
        label: 'Map account',
        longLabel: 'Open Bank Accounts',
        to: '/financials/bank-accounts',
      },
      tone: 'warning',
    };
  }

  // --- Category problems -----------------------------------------------------
  if (/no QB Category assigned/i.test(error)) {
    return {
      kind: 'assign_category',
      headline: 'Pick a category',
      description:
        'Atlas needs a QB category before it can post this transaction. ' +
        'Pick one in the QB Category column (or open the row for AI suggestions).',
      action: {
        kind: 'open_detail',
        label: 'Pick category',
        longLabel: 'Open and pick category',
      },
      tone: 'warning',
    };
  }
  if (/Unsupported Category account_type/i.test(error)) {
    return {
      kind: 'unsupported_category',
      headline: 'Pick a different category',
      description:
        "The picked QB category's account type isn't supported for posting. " +
        'Open the row and pick a different category — the AI panel can suggest one.',
      action: {
        kind: 'open_detail',
        label: 'Change category',
        longLabel: 'Open and pick a different category',
      },
      tone: 'warning',
    };
  }

  // --- Amazon Business -------------------------------------------------------
  if (/Amazon Business order has no categorisable lines/i.test(error)) {
    return {
      kind: 'categorise_amazon_lines',
      headline: 'Categorise Amazon lines',
      description:
        "This transaction is linked to an Amazon Business order whose lines aren't " +
        'categorised. Assign a QB category to the lines on the Amazon Business page.',
      action: {
        kind: 'navigate',
        label: 'Open Amazon',
        longLabel: 'Open Amazon Business',
        to: '/financials/amazon-business',
      },
      tone: 'warning',
    };
  }

  // --- Expense workflow ------------------------------------------------------
  if (/Already in QuickBooks/i.test(error)) {
    return {
      kind: 'mark_not_in_qb',
      headline: 'Flip "Already in QB" off',
      description:
        'This row is marked "Already in QuickBooks" so Atlas refuses to post it. ' +
        'Open the row and change the expense status if you do want Atlas to post it.',
      action: {
        kind: 'open_detail',
        label: 'Open row',
        longLabel: 'Open and change expense status',
      },
      tone: 'info',
    };
  }
  if (/Expense not approved/i.test(error)) {
    return {
      kind: 'approve_expense',
      headline: 'Approve the expense',
      description:
        'The expense needs an approval before Atlas can post it to QuickBooks. ' +
        'Approve it in Expense Tracking, then click Retry.',
      action: {
        kind: 'open_detail',
        label: 'Approve',
        longLabel: 'Open and approve',
      },
      tone: 'info',
    };
  }

  // --- Bank-side failure (defense in depth) ----------------------------------
  if (/Bank reported this transaction/i.test(error)) {
    return {
      kind: 'bank_did_not_settle',
      headline: 'Bank did not settle',
      description:
        'The bank reports this transaction never settled, so there is nothing to ' +
        "post to QuickBooks. If the bank later reclassifies it, Atlas will pick it up.",
      action: { kind: 'none' },
      tone: 'warning',
    };
  }

  // --- Splits ----------------------------------------------------------------
  // The set_transaction_splits RPC validates that splits sum to the
  // parent's amount_cents at write time, so this only fires when an
  // out-of-band write produced an inconsistent split set.
  if (/Splits sum to .* but the transaction is .* cents/i.test(error)) {
    return {
      kind: 'splits_sum_mismatch',
      headline: 'Splits don\u2019t balance',
      description:
        'The split lines on this transaction don\u2019t add up to the transaction ' +
        'amount. Open the row, fix the split amounts so they sum to the total, ' +
        'then click Retry.',
      action: {
        kind: 'open_detail',
        label: 'Fix splits',
        longLabel: 'Open and fix the splits',
      },
      tone: 'warning',
    };
  }
  if (/Splits aren\u2019t supported on (Transfer|Credit Card Payment)/i.test(error) ||
      /Splits aren't supported on (Transfer|Credit Card Payment)/i.test(error)) {
    return {
      kind: 'splits_unsupported_kind',
      headline: 'Splits don\u2019t apply here',
      description:
        "QuickBooks's Transfer and Pay-down-credit-card entities are single-amount " +
        "— they don't accept multi-line splits. Open the row and either clear the " +
        'splits, or pick a different category so it routes to a Purchase / Deposit.',
      action: {
        kind: 'open_detail',
        label: 'Open & fix',
        longLabel: 'Open row to fix the splits',
      },
      tone: 'warning',
    };
  }
  // Per-split chart-of-accounts row missing an external_id (the
  // operator deleted the QBO category between split-time and post-time).
  if (/Split \d+ references a QB category that's not synced/i.test(error)) {
    return {
      kind: 'qbo_reference_invalid',
      headline: 'Split category not in QuickBooks',
      description:
        'One of the split categories was deleted or never synced to QuickBooks. ' +
        'Open the row and pick a different category for that split, or re-sync ' +
        'QuickBooks from the Integrations page.',
      action: {
        kind: 'open_detail',
        label: 'Open & fix',
        longLabel: 'Open row to fix the split category',
      },
      tone: 'warning',
    };
  }

  // --- QBO HTTP errors (everything prefixed with "QBO ") --------------------
  // qboFetch formats every non-2xx response as `QBO <path> <status>: <detail>`.
  if (/^QBO\b/.test(error)) {
    // Look for the "invalid reference Id" family — operator likely deleted the
    // QBO account/vendor/customer after Atlas last synced. Treated as its own
    // bucket so we can recommend re-syncing CoA + picking again.
    if (
      /invalid reference id/i.test(error) ||
      /object reference is not set/i.test(error) ||
      /referenced entity/i.test(error) ||
      /\bnot active\b/i.test(error) ||
      // QBO sometimes says "this entity has been deleted" verbatim.
      /entity has been deleted/i.test(error)
    ) {
      return {
        kind: 'qbo_reference_invalid',
        headline: 'QuickBooks reference stale',
        description:
          'A QuickBooks account or payee Atlas is referencing was deleted or made ' +
          'inactive. Open the row and pick a different category / vendor, or re-sync ' +
          'QuickBooks from the Integrations page.',
        action: {
          kind: 'open_detail',
          label: 'Open & fix',
          longLabel: 'Open row to fix the reference',
        },
        tone: 'warning',
      };
    }
    // QBO 5xx is transient — retry.
    if (/\bQBO\b[^\n]*\b5\d{2}\b/.test(error)) {
      return {
        kind: 'qbo_server',
        headline: 'QuickBooks server error',
        description:
          'QuickBooks returned a server error. This is usually transient — click ' +
          'Retry. The post is idempotent so a duplicate cannot be created.',
        action: {
          kind: 'retry',
          label: 'Retry',
          longLabel: 'Retry post',
        },
        tone: 'warning',
      };
    }
    // QBO Object Not Found (the auto-recreate fallback failed — rare, since
    // the edge function handles 610 transparently on the update path).
    if (/object not found/i.test(error)) {
      return {
        kind: 'qbo_object_missing',
        headline: 'QuickBooks entry missing',
        description:
          'The booked QuickBooks entry is missing and the auto-recreate fallback ' +
          'failed. Click Retry to try again — Atlas will create a fresh entry.',
        action: {
          kind: 'retry',
          label: 'Retry',
          longLabel: 'Retry and recreate',
        },
        tone: 'warning',
      };
    }
    // Everything else from QBO — treat as a validation error the operator
    // needs to inspect on the detail page.
    return {
      kind: 'qbo_validation',
      headline: 'QuickBooks rejected the post',
      description:
        "QuickBooks rejected the post with a validation error. Open the row to see " +
        'the full message and edit the fields it complained about.',
      action: {
        kind: 'open_detail',
        label: 'Edit & retry',
        longLabel: 'Open row to edit & retry',
      },
      tone: 'warning',
    };
  }

  // --- Atlas-side DB / storage failures --------------------------------------
  if (/Could not download receipt from Storage/i.test(error)) {
    return {
      kind: 'storage_error',
      headline: 'Receipt download failed',
      description:
        "Atlas couldn't download the receipt from storage to mirror into QuickBooks. " +
        'Click Retry — the post itself usually succeeds even when the receipt does not.',
      action: {
        kind: 'retry',
        label: 'Retry',
        longLabel: 'Retry post',
      },
      tone: 'warning',
    };
  }
  if (
    /^DB update\b/i.test(error) ||
    /failed to (clear stale|persist) /i.test(error) ||
    /failed to update Atlas row/i.test(error) ||
    /failed to persist link in Atlas/i.test(error)
  ) {
    return {
      kind: 'db_error',
      headline: 'Atlas write failed',
      description:
        "Atlas couldn't record the result of the post. Click Retry — the function " +
        'is idempotent on the QuickBooks entry id, so a duplicate cannot be created.',
      action: {
        kind: 'retry',
        label: 'Retry',
        longLabel: 'Retry post',
      },
      tone: 'warning',
    };
  }

  // --- Fallback --------------------------------------------------------------
  return {
    kind: 'unknown',
    headline: 'Post failed',
    description:
      'The post failed for an unrecognised reason. Click Retry — if it keeps ' +
      'failing, open the row to see the full error message and contact support.',
    action: {
      kind: 'retry',
      label: 'Retry',
      longLabel: 'Retry post',
    },
    tone: 'warning',
  };
}

/**
 * Resolve a `QboPostFailureAction` into something a router-aware UI can
 * render. The `open_detail` action needs the transaction id from the
 * caller (the classifier intentionally doesn't carry it so the same
 * `QboPostFailure` value can be cached across rows). Returns null when
 * the action has no target (the `none` case).
 */
export function resolveFailureActionHref(
  action: QboPostFailureAction,
  transactionId: string,
): string | null {
  if (action.kind === 'navigate') return action.to;
  if (action.kind === 'open_detail') return `/financials/transactions/${transactionId}`;
  return null;
}
